'use client';

import { c as _c } from "react/compiler-runtime";
import { MessageBox } from '@7f-ui/components/message-box/message-box';
import { useToast } from '@7f-ui/components/toast/toast-provider';
import { AddedToCart, addedToCartItemsAtom, buildExperienceCartItem } from '@feat/feat-cart/added-to-cart';
import { isCartCurrencyMismatchError, useIsCartCurrencyMismatch } from '@feat/feat-cart/currency';
import { useCartItem } from '@feat/feat-cart/item';
import { trackClickCta } from '@feat/feat-tracking/cta';
import { useAtom, useSetAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useAppExperienceDayPrices } from '../_hooks/use-app-experience-day-prices';
import { useExperienceOptions } from '../_hooks/use-app-experience-options';
import { useAppExperiencePurchasableDays } from '../_hooks/use-app-experience-purchasable-days';
import { useAppExperienceRecommendation } from '../_hooks/use-app-experience-recommendation';
import { useAppTicketSelection, useOpeningHoursLabel } from '../_hooks/use-app-ticket-selection';
import { ticketPreviewDays } from '../_lib/ticket-calendar';
import { selectedTicketDayAtom, ticketDialogAtom } from '../_lib/ticket-flow-atoms';
import { useExperienceFilterContext } from './app-experience-filter-provider';
import { AppExperienceTicketDays } from './app-experience-ticket-days';
import { AppExperienceTicketDaysSkeleton } from './app-experience-ticket-days-skeleton';
import { AppPersonModal } from './app-person-modal';
import { AppTicketDatePicker } from './app-ticket-date-picker';
import { AppTicketSelectionSheet } from './app-ticket-selection-sheet';
export function AppExperienceTicketSection() {
  const $ = _c(84);
  const toast = useToast();
  const {
    experience,
    baseVariants,
    filter,
    dayPriceIsPreliminary,
    dayPriceFailed
  } = useExperienceFilterContext();
  const {
    addExperience
  } = useCartItem();
  const setAddedToCartItems = useSetAtom(addedToCartItemsAtom);
  const {
    allOptions
  } = useExperienceOptions(baseVariants);
  const {
    ticketsBreakdown
  } = useAppTicketSelection();
  const getOpeningHoursLabel = useOpeningHoursLabel();
  const {
    recommendation,
    loadRecommendation
  } = useAppExperienceRecommendation(experience.publicId);
  const isAlwaysAvailable = experience.alwaysAvailable;
  const {
    purchasableDays,
    isLoading,
    isError
  } = useAppExperiencePurchasableDays(experience, !isAlwaysAvailable);
  const [activeDialog, setActiveDialog] = useAtom(ticketDialogAtom);
  const [selectedDay, setSelectedDay] = useAtom(selectedTicketDayAtom);
  const [isSubmitting, setIsSubmitting] = useState(false);
  let t0;
  let t1;
  if ($[0] !== setActiveDialog || $[1] !== setSelectedDay) {
    t0 = () => () => {
      setActiveDialog(null);
      setSelectedDay(null);
    };
    t1 = [setActiveDialog, setSelectedDay];
    $[0] = setActiveDialog;
    $[1] = setSelectedDay;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[4] !== allOptions) {
    t3 = allOptions.filter(_temp);
    $[4] = allOptions;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const paidOptions = t3;
  const candidates = paidOptions.length > 0 ? paidOptions : allOptions;
  let t4;
  if ($[6] !== candidates) {
    t4 = candidates.toSorted(_temp2);
    $[6] = candidates;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  t2 = t4[0];
  const cheapestOption = t2;
  let t5;
  if ($[8] !== filter || $[9] !== purchasableDays) {
    t5 = ticketPreviewDays(purchasableDays, filter);
    $[8] = filter;
    $[9] = purchasableDays;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const previewDays = t5;
  const {
    dayPrices,
    isError: previewPricesFailed,
    arePreliminary: previewPricesArePreliminary
  } = useAppExperienceDayPrices(experience.id, previewDays[0]?.date, previewDays[previewDays.length - 1]?.date);
  let t6;
  if ($[11] !== addExperience || $[12] !== dayPriceFailed || $[13] !== dayPriceIsPreliminary || $[14] !== experience || $[15] !== filter.selectedOptions || $[16] !== getOpeningHoursLabel || $[17] !== loadRecommendation || $[18] !== selectedDay || $[19] !== setActiveDialog || $[20] !== setAddedToCartItems || $[21] !== ticketsBreakdown || $[22] !== toast) {
    t6 = () => {
      if (!filter.selectedOptions) {
        return;
      }
      if (dayPriceIsPreliminary || dayPriceFailed) {
        return;
      }
      setIsSubmitting(true);
      addExperience({
        experienceId: experience.id,
        visitDate: selectedDay?.date ?? null,
        selectedOptions: filter.selectedOptions
      }, {
        onSuccess: () => {
          setAddedToCartItems([buildExperienceCartItem(experience, {
            startDate: selectedDay?.date ?? "",
            timeRange: getOpeningHoursLabel(selectedDay),
            tickets: ticketsBreakdown
          })]);
          loadRecommendation();
          setIsSubmitting(false);
          setActiveDialog(null);
        },
        onError: error => {
          if (!isCartCurrencyMismatchError(error)) {
            toast({
              type: "error",
              message: "Die Eintrittskarte konnte nicht in den Warenkorb gelegt werden. Bitte versuche es noch einmal."
            });
          }
          setIsSubmitting(false);
        }
      });
    };
    $[11] = addExperience;
    $[12] = dayPriceFailed;
    $[13] = dayPriceIsPreliminary;
    $[14] = experience;
    $[15] = filter.selectedOptions;
    $[16] = getOpeningHoursLabel;
    $[17] = loadRecommendation;
    $[18] = selectedDay;
    $[19] = setActiveDialog;
    $[20] = setAddedToCartItems;
    $[21] = ticketsBreakdown;
    $[22] = toast;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const handleSubmit = t6;
  const hasNoOpenDay = !isLoading && !isAlwaysAvailable && previewDays.length === 0;
  const showLoadError = isError || dayPriceFailed || previewPricesFailed;
  const needsVisitDate = !isAlwaysAvailable && !selectedDay;
  const isCurrencyMismatch = useIsCartCurrencyMismatch(experience.currency);
  let t7;
  if ($[24] !== isLoading) {
    t7 = isLoading && <AppExperienceTicketDaysSkeleton />;
    $[24] = isLoading;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== hasNoOpenDay || $[27] !== showLoadError) {
    t8 = (hasNoOpenDay || showLoadError) && <section className="py-8" data-testid="TicketDays.empty"><h2 className="mb-6 font-heading3">Eintrittskarten</h2><MessageBox type={showLoadError ? "error" : "info"}>{showLoadError ? "Die \xD6ffnungstage konnten gerade nicht geladen werden. Bitte lade die Seite neu." : "F\xFCr den gew\xE4hlten Zeitraum sind keine \xD6ffnungstage hinterlegt. Bitte w\xE4hle einen anderen Zeitraum."}</MessageBox></section>;
    $[26] = hasNoOpenDay;
    $[27] = showLoadError;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== cheapestOption?.price || $[30] !== dayPrices || $[31] !== experience.currency || $[32] !== isAlwaysAvailable || $[33] !== isLoading || $[34] !== previewDays || $[35] !== previewPricesArePreliminary || $[36] !== selectedDay?.date || $[37] !== setActiveDialog || $[38] !== setSelectedDay || $[39] !== showLoadError) {
    t9 = !isLoading && !showLoadError && !isAlwaysAvailable && <AppExperienceTicketDays days={previewDays} basePrice={previewPricesArePreliminary ? undefined : cheapestOption?.price} currency={experience.currency} pricesByDate={previewPricesArePreliminary ? undefined : dayPrices} selectedDate={selectedDay?.date ?? null} onSelectDay={day => {
      trackClickCta("choose available date", {
        type: "experience",
        selection: day.date
      });
      setSelectedDay(day);
      setActiveDialog("sheet");
    }} onShowAll={() => {
      trackClickCta("open calendar - on site", {
        type: "experience"
      });
      setActiveDialog("date");
    }} />;
    $[29] = cheapestOption?.price;
    $[30] = dayPrices;
    $[31] = experience.currency;
    $[32] = isAlwaysAvailable;
    $[33] = isLoading;
    $[34] = previewDays;
    $[35] = previewPricesArePreliminary;
    $[36] = selectedDay?.date;
    $[37] = setActiveDialog;
    $[38] = setSelectedDay;
    $[39] = showLoadError;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  let t10;
  if ($[41] !== recommendation) {
    t10 = <AddedToCart recommendation={recommendation} />;
    $[41] = recommendation;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  const t11 = activeDialog === "sheet";
  const t12 = needsVisitDate || isCurrencyMismatch || dayPriceIsPreliminary || dayPriceFailed;
  const t13 = !isAlwaysAvailable;
  let t14;
  let t15;
  let t16;
  if ($[43] !== setActiveDialog) {
    t14 = () => setActiveDialog(null);
    t15 = () => setActiveDialog("tickets");
    t16 = () => setActiveDialog("date");
    $[43] = setActiveDialog;
    $[44] = t14;
    $[45] = t15;
    $[46] = t16;
  } else {
    t14 = $[44];
    t15 = $[45];
    t16 = $[46];
  }
  let t17;
  if ($[47] !== handleSubmit || $[48] !== isSubmitting || $[49] !== selectedDay || $[50] !== t11 || $[51] !== t12 || $[52] !== t13 || $[53] !== t14 || $[54] !== t15 || $[55] !== t16) {
    t17 = <AppTicketSelectionSheet isOpen={t11} day={selectedDay} submitLabel="In den Warenkorb" isSubmitting={isSubmitting} disabled={t12} showDateRow={t13} onClose={t14} onEditTickets={t15} onEditDate={t16} onSubmit={handleSubmit} />;
    $[47] = handleSubmit;
    $[48] = isSubmitting;
    $[49] = selectedDay;
    $[50] = t11;
    $[51] = t12;
    $[52] = t13;
    $[53] = t14;
    $[54] = t15;
    $[55] = t16;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  const t18 = activeDialog === "tickets";
  let t19;
  if ($[57] !== needsVisitDate || $[58] !== setActiveDialog) {
    t19 = () => setActiveDialog(needsVisitDate ? "date" : "sheet");
    $[57] = needsVisitDate;
    $[58] = setActiveDialog;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] !== setActiveDialog) {
    t20 = () => setActiveDialog(null);
    $[60] = setActiveDialog;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  let t21;
  if ($[62] !== t18 || $[63] !== t19 || $[64] !== t20) {
    t21 = <AppPersonModal isOpen={t18} title="Wer kommt mit?" showContactFormLink={false} onSubmit={t19} onClose={t20} />;
    $[62] = t18;
    $[63] = t19;
    $[64] = t20;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  const t22 = activeDialog === "date";
  const t23 = isCurrencyMismatch || dayPriceIsPreliminary || dayPriceFailed;
  let t24;
  if ($[66] !== setActiveDialog) {
    t24 = () => setActiveDialog(null);
    $[66] = setActiveDialog;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  let t25;
  if ($[68] !== handleSubmit || $[69] !== isSubmitting || $[70] !== selectedDay || $[71] !== setSelectedDay || $[72] !== t22 || $[73] !== t23 || $[74] !== t24) {
    t25 = <AppTicketDatePicker isOpen={t22} selectedDay={selectedDay} submitLabel="In den Warenkorb" isSubmitting={isSubmitting} disabled={t23} onClose={t24} onSelect={setSelectedDay} onSubmit={handleSubmit} />;
    $[68] = handleSubmit;
    $[69] = isSubmitting;
    $[70] = selectedDay;
    $[71] = setSelectedDay;
    $[72] = t22;
    $[73] = t23;
    $[74] = t24;
    $[75] = t25;
  } else {
    t25 = $[75];
  }
  let t26;
  if ($[76] !== t10 || $[77] !== t17 || $[78] !== t21 || $[79] !== t25 || $[80] !== t7 || $[81] !== t8 || $[82] !== t9) {
    t26 = <>{t7}{t8}{t9}{t10}{t17}{t21}{t25}</>;
    $[76] = t10;
    $[77] = t17;
    $[78] = t21;
    $[79] = t25;
    $[80] = t7;
    $[81] = t8;
    $[82] = t9;
    $[83] = t26;
  } else {
    t26 = $[83];
  }
  return t26;
}
function _temp2(a, b) {
  return a.price - b.price;
}
function _temp(option) {
  return option.price > 0;
}